import { load as loadYaml } from 'js-yaml';

const HTTP_METHODS = ['get', 'post', 'put', 'delete', 'patch', 'options', 'head'];

export function parseOpenApiSpec(specInput) {
  if (!specInput) {
    throw new Error('OpenAPI spec input is empty.');
  }

  const parsed =
    typeof specInput === 'string'
      ? parseStringSpec(specInput)
      : Array.isArray(specInput)
      ? null
      : specInput;

  if (!parsed || typeof parsed !== 'object') {
    throw new Error('Unable to parse OpenAPI spec. Provide valid JSON or YAML.');
  }

  if (isPostmanCollection(parsed)) {
    return convertPostmanCollectionToSpec(parsed);
  }

  if (!parsed.openapi || !parsed.paths) {
    throw new Error('Spec must include openapi version and paths.');
  }

  return parsed;
}

export function addMissingApiRoutes(spec, routes) {
  const paths = { ...(spec.paths || {}) };
  const groupNames = {
    business: 'Business',
    waba: 'WhatsApp Business Accounts',
    messages: 'Messages',
    templates: 'Templates',
    media: 'Media',
    'qr-codes': 'QR codes',
    'phone-numbers': 'Phone Numbers',
    users: 'Blocked Users',
    'business-profiles': 'Business Profiles',
    commerce: 'Commerce',
    flows: 'Flows',
    uploads: 'Uploads'
  };
  const methodActions = {
    get: 'Retrieve',
    post: 'Submit',
    put: 'Update',
    delete: 'Delete',
    patch: 'Update',
    options: 'Inspect options for',
    head: 'Inspect'
  };

  routes.forEach(({ method, path }) => {
    if (!HTTP_METHODS.includes(method) || typeof path !== 'string' || !path.startsWith('/')) {
      throw new Error(`Invalid supplemental API route: ${method} ${path}`);
    }

    const pathItem = { ...(paths[path] || {}) };
    if (pathItem[method]) {
      paths[path] = pathItem;
      return;
    }

    const pathParts = path
      .replace(/^\/v1\/?/, '')
      .split('/')
      .filter(Boolean)
      .map((part) => {
        if (part.startsWith('{') && part.endsWith('}')) {
          const name = part.slice(1, -1).replace(/([a-z])([A-Z])/g, '$1 $2');
          return `by ${name.replace(/\b(id|waba)\b/gi, (value) => value.toUpperCase())}`;
        }
        return part.replace(/[-_]/g, ' ');
      });
    const routeLabel = pathParts.join(' ');
    const summary = `${methodActions[method] || 'Use'} ${routeLabel}`;
    const group = groupNames[path.split('/')[2]] || 'API';
    const parameters = [...path.matchAll(/\{([^}]+)\}/g)].map((match) => ({
      name: match[1],
      in: 'path',
      description: `${match[1]} path value.`,
      required: true,
      schema: { type: 'string' }
    }));

    pathItem[method] = {
      tags: [group],
      summary,
      description: `${summary}. This route was added from the requested Hawktrion API endpoint list. That list does not specify request or response schemas.`,
      operationId: `${method}_${path.replace(/[^a-zA-Z0-9]+/g, '_').replace(/^_|_$/g, '')}`,
      parameters,
      responses: {
        default: {
          description: 'Response schema is not specified in the supplied endpoint list.'
        }
      }
    };
    paths[path] = pathItem;
  });

  return { ...spec, paths };
}

export function filterApiRoutes(spec, routes) {
  const paths = {};

  routes.forEach(({ method, path }) => {
    const operation = spec.paths?.[path]?.[method];
    if (!operation) {
      throw new Error(`Requested API route is missing from the spec: ${method.toUpperCase()} ${path}`);
    }
    paths[path] = { ...(paths[path] || {}), [method]: operation };
  });

  return { ...spec, paths };
}

function parseStringSpec(text) {
  try {
    return JSON.parse(text);
  } catch (_jsonError) {
    try {
      return loadYaml(text);
    } catch (_yamlError) {
      return null;
    }
  }
}

function isPostmanCollection(parsed) {
  return Array.isArray(parsed?.item) && typeof parsed?.info?.schema === 'string' && parsed.info.schema.includes('postman');
}

function convertPostmanCollectionToSpec(collection) {
  const servers = new Map();
  const operations = new Map();

  walkPostmanItems(collection.item || [], [], (item, parentNames) => {
    const request = item?.request;
    if (!request) {
      return;
    }

    const methodLower = String(request.method || '').toLowerCase();
    if (!HTTP_METHODS.includes(methodLower)) {
      return;
    }

    const normalizedUrl = normalizePostmanUrl(request.url);
    if (!normalizedUrl.path) {
      return;
    }

    if (normalizedUrl.server && !servers.has(normalizedUrl.server)) {
      servers.set(normalizedUrl.server, {
        url: normalizedUrl.server,
        description: normalizedUrl.server === 'https://api-waba.hawktrion.com' ? 'WABA API' : ''
      });
    }

    const key = `${methodLower}:${normalizedUrl.path}`;
    const tag = parentNames[0] || 'General';
    const requestExample = extractRequestExample(item, request);
    const responseMap = buildResponsesFromExamples(item.response || []);
    const parameters = [
      ...buildPathParameters(normalizedUrl.path),
      ...buildQueryParameters(normalizedUrl.query),
      ...buildHeaderParameters(request.header || [])
    ];

    if (!operations.has(key)) {
      operations.set(key, {
        id: key,
        method: methodLower.toUpperCase(),
        methodLower,
        path: normalizedUrl.path,
        summary: item.name || `${methodLower.toUpperCase()} ${normalizedUrl.path}`,
        description: request.description || item.description || '',
        tags: [tag],
        parameters,
        requestBody: requestExample.requestBody,
        responses: responseMap,
        requestExamples: requestExample.example ? [requestExample.example] : [],
        operation: {
          source: 'postman',
          sourceNames: [item.name].filter(Boolean)
        }
      });
      return;
    }

    const existing = operations.get(key);
    existing.tags = dedupeStrings([...existing.tags, tag]);
    existing.parameters = mergeParameters(existing.parameters || [], parameters);
    existing.requestBody = mergeRequestBodies(existing.requestBody, requestExample.requestBody);
    existing.responses = mergeResponses(existing.responses, responseMap);
    existing.requestExamples = mergeRequestExamples(existing.requestExamples || [], requestExample.example);

    if (!existing.description && (request.description || item.description)) {
      existing.description = request.description || item.description || '';
    }

    if (item.name && !existing.operation.sourceNames.includes(item.name)) {
      existing.operation.sourceNames.push(item.name);
    }
  });

  const paths = {};

  operations.forEach((operation) => {
    if (!paths[operation.path]) {
      paths[operation.path] = {};
    }

    paths[operation.path][operation.methodLower] = {
      tags: operation.tags,
      summary: operation.summary,
      description: operation.description,
      parameters: operation.parameters,
      requestBody: operation.requestBody,
      responses: operation.responses,
      requestExamples: operation.requestExamples,
      sourceNames: operation.operation.sourceNames
    };
  });

  return {
    openapi: '3.1.0',
    info: {
      title: collection.info?.name || 'Imported API Collection',
      version: collection.info?._postman_id || 'postman-import',
      description: collection.info?.description || ''
    },
    servers: [...servers.values()],
    paths
  };
}

function walkPostmanItems(items, parentNames, visit) {
  items.forEach((item) => {
    if (Array.isArray(item.item)) {
      walkPostmanItems(item.item, [...parentNames, item.name].filter(Boolean), visit);
      return;
    }

    visit(item, parentNames);
  });
}

function normalizePostmanUrl(url) {
  if (!url) {
    return { server: '', path: '', query: [] };
  }

  if (typeof url === 'string') {
    return {
      server: '',
      path: replacePostmanTokens(url),
      query: []
    };
  }

  const protocol = url.protocol || 'https';
  const hostParts = Array.isArray(url.host) ? url.host : [];
  const server = hostParts.length ? `${protocol}://${hostParts.join('.')}` : '';
  const pathParts = Array.isArray(url.path) ? url.path : [];
  const rawPath = extractPathFromRawUrl(url.raw);
  const pathFromParts = `/${pathParts.map((part) => replacePostmanTokens(String(part))).join('/')}`.replace(/\/+/g, '/');
  const path = normalizeKnownProxyPath(replacePostmanTokens(rawPath || pathFromParts));
  const query = Array.isArray(url.query)
    ? url.query
        .filter((entry) => entry && entry.key)
        .map((entry) => ({
          name: entry.key,
          value: replacePostmanTokens(entry.value ?? ''),
          description: entry.description || ''
        }))
    : [];

  return {
    server,
    path,
    query
  };
}

function extractPathFromRawUrl(rawUrl) {
  if (typeof rawUrl !== 'string' || !rawUrl.trim()) {
    return '';
  }

  const withoutHost = rawUrl.replace(/^[a-z]+:\/\/[^/]+/i, '');
  const onlyPath = withoutHost.split('?')[0] || '';
  if (!onlyPath) {
    return '';
  }

  return onlyPath.startsWith('/') ? onlyPath : `/${onlyPath}`;
}

function normalizeKnownProxyPath(path) {
  const safePath = String(path || '').replace(/\/+/g, '/');

  if (/^\/\{Version\}\/\{Phone-Number-ID\}\/messages$/i.test(safePath)) {
    return '/v1/messages';
  }

  if (/^\/v\d+(\.\d+)?\/\{Phone-Number-ID\}\/messages$/i.test(safePath)) {
    return '/v1/messages';
  }

  return safePath.replace(/^\/\{Version\}(?=\/|$)/i, '/v1') || '/';
}

function replacePostmanTokens(value) {
  return String(value).replace(/\{\{\s*([^}]+?)\s*\}\}/g, '{$1}');
}

function buildPathParameters(path) {
  const matches = [...path.matchAll(/\{([^}]+)\}/g)];
  return matches.map((match) => ({
    name: match[1],
    in: 'path',
    description: '',
    required: true,
    schema: {
      type: 'string'
    }
  }));
}

function buildQueryParameters(query) {
  return query.map((entry) => ({
    name: entry.name,
    in: 'query',
    description: entry.description || '',
    required: false,
    schema: inferSchemaFromValue(entry.value)
  }));
}

function buildHeaderParameters(headers) {
  return headers
    .filter((header) => header && header.key && !String(header.disabled).toLowerCase().includes('true'))
    .map((header) => ({
      name: header.key,
      in: 'header',
      description: header.description || '',
      required: false,
      schema: inferSchemaFromValue(header.value ?? '')
    }));
}

function extractRequestExample(item, request) {
  const body = request.body || null;
  const mediaType = getRequestMediaType(request.header || [], body);

  if (!body) {
    return {
      example: null,
      requestBody: undefined
    };
  }

  if (body.mode === 'raw') {
    const raw = body.raw?.trim() || '';
    const parsed = safeJsonParse(raw);
    const schema = inferSchemaFromValue(parsed ?? raw);

    return {
      example: {
        id: createExampleId(item.name, raw || item.request?.url?.raw || item.name),
        name: item.name || 'Example payload',
        description: request.description || item.description || '',
        mediaType,
        bodyText: raw || '{}',
        body: parsed
      },
      requestBody: raw
        ? {
            required: true,
            content: {
              [mediaType]: {
                schema
              }
            }
          }
        : undefined
    };
  }

  return {
    example: null,
    requestBody: undefined
  };
}

function getRequestMediaType(headers, body) {
  const contentTypeHeader = headers.find((header) => header?.key?.toLowerCase() === 'content-type');
  if (contentTypeHeader?.value) {
    return contentTypeHeader.value;
  }

  if (body?.mode === 'raw') {
    return body.options?.raw?.language === 'json' ? 'application/json' : 'text/plain';
  }

  return 'application/json';
}

function buildResponsesFromExamples(responseExamples) {
  const responses = {};

  responseExamples.forEach((response, index) => {
    const statusKey = String(response.code || response.status || `example-${index + 1}`);
    if (responses[statusKey]) {
      return;
    }

    const bodyText = response.body || '';
    const parsedBody = safeJsonParse(bodyText);
    const mediaType = getResponseMediaType(response.header || []);

    responses[statusKey] = {
      description: response.name || response.status || 'Response example',
      content:
        bodyText !== ''
          ? {
              [mediaType]: {
                schema: inferSchemaFromValue(parsedBody ?? bodyText)
              }
            }
          : {}
    };
  });

  if (!Object.keys(responses).length) {
    return {
      default: {
        description: 'No response examples provided.',
        content: {}
      }
    };
  }

  return responses;
}

function getResponseMediaType(headers) {
  const contentTypeHeader = headers.find((header) => header?.key?.toLowerCase() === 'content-type');
  return contentTypeHeader?.value || 'application/json';
}

function safeJsonParse(value) {
  if (typeof value !== 'string' || !value.trim()) {
    return null;
  }

  try {
    return JSON.parse(value);
  } catch (_error) {
    return null;
  }
}

function inferSchemaFromValue(value) {
  if (value === null || value === undefined) {
    return { type: 'string' };
  }

  if (Array.isArray(value)) {
    return {
      type: 'array',
      items: value.length ? inferSchemaFromValue(value[0]) : { type: 'string' }
    };
  }

  if (typeof value === 'object') {
    const properties = {};
    Object.entries(value).forEach(([key, nestedValue]) => {
      properties[key] = inferSchemaFromValue(nestedValue);
    });

    return {
      type: 'object',
      properties
    };
  }

  if (typeof value === 'number') {
    return {
      type: Number.isInteger(value) ? 'integer' : 'number',
      example: value
    };
  }

  if (typeof value === 'boolean') {
    return {
      type: 'boolean',
      example: value
    };
  }

  return {
    type: 'string',
    example: value
  };
}

function mergeRequestBodies(existingBody, nextBody) {
  if (!existingBody) {
    return nextBody;
  }

  if (!nextBody) {
    return existingBody;
  }

  const existingContent = existingBody.content || {};
  const nextContent = nextBody.content || {};
  const mergedContent = { ...existingContent };

  Object.entries(nextContent).forEach(([mediaType, value]) => {
    if (!mergedContent[mediaType]) {
      mergedContent[mediaType] = value;
      return;
    }

    mergedContent[mediaType] = {
      ...mergedContent[mediaType],
      schema: mergeSchemas(mergedContent[mediaType].schema, value.schema)
    };
  });

  return {
    ...existingBody,
    content: mergedContent
  };
}

function mergeResponses(existingResponses, nextResponses) {
  const merged = { ...(existingResponses || {}) };

  Object.entries(nextResponses || {}).forEach(([status, response]) => {
    if (!merged[status]) {
      merged[status] = response;
      return;
    }

    const existingContent = merged[status].content || {};
    const nextContent = response.content || {};
    const content = { ...existingContent };

    Object.entries(nextContent).forEach(([mediaType, media]) => {
      if (!content[mediaType]) {
        content[mediaType] = media;
        return;
      }

      content[mediaType] = {
        ...content[mediaType],
        schema: mergeSchemas(content[mediaType].schema, media.schema)
      };
    });

    merged[status] = {
      ...merged[status],
      content
    };
  });

  return merged;
}

function mergeSchemas(existingSchema, nextSchema) {
  if (!existingSchema) {
    return nextSchema;
  }

  if (!nextSchema) {
    return existingSchema;
  }

  if (existingSchema.type !== nextSchema.type) {
    return {
      oneOf: [existingSchema, nextSchema]
    };
  }

  if (existingSchema.type === 'object') {
    const properties = { ...(existingSchema.properties || {}) };

    Object.entries(nextSchema.properties || {}).forEach(([key, value]) => {
      properties[key] = mergeSchemas(properties[key], value);
    });

    return {
      type: 'object',
      properties
    };
  }

  if (existingSchema.type === 'array') {
    return {
      type: 'array',
      items: mergeSchemas(existingSchema.items, nextSchema.items)
    };
  }

  return existingSchema.example !== undefined ? existingSchema : nextSchema;
}

function mergeRequestExamples(existingExamples, nextExample) {
  if (!nextExample) {
    return existingExamples;
  }

  const duplicate = existingExamples.find(
    (example) => example.name === nextExample.name || (example.bodyText && example.bodyText === nextExample.bodyText)
  );

  if (duplicate) {
    return existingExamples;
  }

  return [...existingExamples, nextExample];
}

function createExampleId(name, seed) {
  const normalized = `${name || 'example'}-${seed || ''}`.toLowerCase().replace(/[^a-z0-9]+/g, '-');
  return normalized.replace(/^-+|-+$/g, '') || 'example';
}

function dedupeStrings(values) {
  return [...new Set(values.filter(Boolean))];
}

export function normalizeOperations(spec) {
  const operations = [];
  const paths = spec.paths || {};

  Object.entries(paths).forEach(([path, pathItem]) => {
    const pathParams = Array.isArray(pathItem.parameters) ? pathItem.parameters : [];

    HTTP_METHODS.forEach((method) => {
      const operation = pathItem[method];
      if (!operation) {
        return;
      }

      const tags = operation.tags?.length ? operation.tags : ['General'];
      operations.push({
        id: `${method}:${path}`,
        method: method.toUpperCase(),
        methodLower: method,
        path,
        summary: operation.summary || `${method.toUpperCase()} ${path}`,
        description: operation.description || operation.summary || '',
        tags,
        parameters: mergeParameters(pathParams, operation.parameters || []),
        requestBody: operation.requestBody,
        responses: operation.responses || {},
        requestExamples: operation.requestExamples || [],
        operation
      });
    });
  });

  return operations;
}

function mergeParameters(pathParams, operationParams) {
  const byKey = new Map();

  pathParams.forEach((param) => {
    byKey.set(`${param.in}:${param.name}`, param);
  });

  operationParams.forEach((param) => {
    byKey.set(`${param.in}:${param.name}`, param);
  });

  return [...byKey.values()];
}

export function groupOperationsByTag(operations) {
  const grouped = {};

  operations.forEach((operation) => {
    operation.tags.forEach((tag) => {
      if (!grouped[tag]) {
        grouped[tag] = [];
      }
      grouped[tag].push(operation);
    });
  });

  return grouped;
}

export function resolveRef(spec, ref) {
  if (!ref || typeof ref !== 'string' || !ref.startsWith('#/')) {
    return null;
  }

  const parts = ref.replace('#/', '').split('/');
  let current = spec;

  for (const part of parts) {
    if (!current || typeof current !== 'object') {
      return null;
    }
    current = current[part];
  }

  return current || null;
}

export function resolveSchema(spec, schema) {
  if (!schema) {
    return null;
  }

  if (schema.$ref) {
    return resolveRef(spec, schema.$ref);
  }

  return schema;
}

export function getPrimaryContent(content = {}) {
  if (!content || typeof content !== 'object') {
    return { mediaType: null, schema: null };
  }

  const preferred = ['application/json', 'application/*+json'];
  const mediaTypes = Object.keys(content);

  for (const candidate of preferred) {
    if (content[candidate]) {
      return {
        mediaType: candidate,
        schema: content[candidate].schema || null
      };
    }
  }

  const first = mediaTypes[0];
  return {
    mediaType: first || null,
    schema: first ? content[first]?.schema || null : null
  };
}

export function buildExampleFromSchema(spec, schema, depth = 0) {
  if (!schema || depth > 6) {
    return null;
  }

  const resolved = resolveSchema(spec, schema) || schema;

  if (resolved.example !== undefined) {
    return resolved.example;
  }

  if (resolved.default !== undefined) {
    return resolved.default;
  }

  if (resolved.enum?.length) {
    return resolved.enum[0];
  }

  if (resolved.type === 'object' || resolved.properties) {
    const objectValue = {};
    const properties = resolved.properties || {};

    Object.entries(properties).forEach(([propName, propSchema]) => {
      objectValue[propName] = buildExampleFromSchema(spec, propSchema, depth + 1);
    });

    return objectValue;
  }

  if (resolved.type === 'array') {
    const itemExample = buildExampleFromSchema(spec, resolved.items, depth + 1);
    return itemExample === null ? [] : [itemExample];
  }

  switch (resolved.type) {
    case 'integer':
    case 'number':
      return 0;
    case 'boolean':
      return false;
    case 'string':
      if (resolved.format === 'date-time') {
        return new Date().toISOString();
      }
      if (resolved.format === 'uri') {
        return 'https://example.test';
      }
      return '';
    default:
      return null;
  }
}

export function getSchemaType(schema) {
  if (!schema) {
    return 'unknown';
  }

  if (schema.$ref) {
    return schema.$ref.split('/').pop() || 'ref';
  }

  if (schema.type) {
    if (schema.type === 'array' && schema.items) {
      return `array<${getSchemaType(schema.items)}>`;
    }
    return schema.type;
  }

  if (schema.oneOf) {
    return 'oneOf';
  }

  if (schema.anyOf) {
    return 'anyOf';
  }

  return 'object';
}
